import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { candidateService } from '../../services/api/candidateService';
import {
    Candidate,
    CandidateCreateRequest,
    CandidateUpdateRequest,
    StageChangeRequest,
    Document,
    RecentActivity,
    Note,
    PaginatedResult
} from '../../types/types';

interface CandidatesState {
    candidates: Candidate[];
    currentCandidate: Candidate | null;
    recentCandidates: Candidate[];
    candidatesByStage: Record<string, Candidate[]>;
    candidatesByJob: Record<string, Candidate[]>;

    // Document management
    documents: Record<string, Document[]>; // candidateId -> documents

    // Activities and notes
    activities: Record<string, RecentActivity[]>; // candidateId -> activities
    notes: Record<string, Note[]>; // candidateId -> notes

    // Loading states
    loading: boolean;
    candidateLoading: boolean;
    documentsLoading: Record<string, boolean>;
    activitiesLoading: Record<string, boolean>;
    notesLoading: Record<string, boolean>;

    // Error states
    error: string | null;
    candidateError: string | null;

    // Pagination and filtering
    pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        pageSize: number;
    };

    // Cache management
    lastFetched: Record<string, number>; // key -> timestamp
    searchCache: Record<string, Candidate[]>; // searchTerm -> results
}

const initialState: CandidatesState = {
    candidates: [],
    currentCandidate: null,
    recentCandidates: [],
    candidatesByStage: {},
    candidatesByJob: {},
    documents: {},
    activities: {},
    notes: {},
    loading: false,
    candidateLoading: false,
    documentsLoading: {},
    activitiesLoading: {},
    notesLoading: {},
    error: null,
    candidateError: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        pageSize: 20
    },
    lastFetched: {},
    searchCache: {}
};

// Async thunks
export const fetchCandidates = createAsyncThunk(
    'candidates/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const result = await candidateService.getCandidates();
            return result;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch candidates');
        }
    }
);

export const fetchRecentCandidates = createAsyncThunk(
    'candidates/fetchRecent',
    async (_, { rejectWithValue }) => {
        try {
            const candidates = await candidateService.getRecentCandidates();
            return candidates;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch recent candidates');
        }
    }
);

export const fetchCandidatesByStage = createAsyncThunk(
    'candidates/fetchByStage',
    async (stageId: string, { rejectWithValue }) => {
        try {
            const candidates = await candidateService.getCandidatesByStage(stageId);
            return { stageId, candidates };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch candidates by stage');
        }
    }
);

export const fetchCandidatesByJob = createAsyncThunk(
    'candidates/fetchByJob',
    async (jobId: string, { rejectWithValue }) => {
        try {
            const candidates = await candidateService.getCandidatesByJob(jobId);
            return { jobId, candidates };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch candidates by job');
        }
    }
);

export const fetchCandidateById = createAsyncThunk(
    'candidates/fetchById',
    async (candidateId: string, { rejectWithValue }) => {
        try {
            const candidate = await candidateService.getCandidateById(candidateId);
            return candidate;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch candidate');
        }
    }
);

export const createCandidate = createAsyncThunk(
    'candidates/create',
    async (candidateData: CandidateCreateRequest, { rejectWithValue }) => {
        try {
            const candidate = await candidateService.createCandidate(candidateData);
            return candidate;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to create candidate');
        }
    }
);

export const updateCandidate = createAsyncThunk(
    'candidates/update',
    async ({ id, candidateData }: { id: string; candidateData: CandidateUpdateRequest }, { rejectWithValue }) => {
        try {
            const candidate = await candidateService.updateCandidate(id, candidateData);
            return candidate;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update candidate');
        }
    }
);

export const deleteCandidate = createAsyncThunk(
    'candidates/delete',
    async (candidateId: string, { rejectWithValue }) => {
        try {
            await candidateService.deleteCandidate(candidateId);
            return candidateId;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete candidate');
        }
    }
);

export const changeCandidateStage = createAsyncThunk(
    'candidates/changeStage',
    async ({ candidateId, stageChangeData }: { candidateId: string; stageChangeData: StageChangeRequest }, { rejectWithValue }) => {
        try {
            const candidate = await candidateService.changeStage(candidateId, stageChangeData);
            return { candidate, oldStageId: stageChangeData.candidateId, newStageId: stageChangeData.newStageId };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to change candidate stage');
        }
    }
);

// Document management
export const fetchCandidateDocuments = createAsyncThunk(
    'candidates/fetchDocuments',
    async (candidateId: string, { rejectWithValue }) => {
        try {
            const documents = await candidateService.getCandidateDocuments(candidateId);
            return { candidateId, documents };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch documents');
        }
    }
);

export const uploadCandidateDocument = createAsyncThunk(
    'candidates/uploadDocument',
    async ({ candidateId, file }: { candidateId: string; file: File }, { rejectWithValue }) => {
        try {
            const document = await candidateService.uploadDocument(candidateId, file);
            return { candidateId, document };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to upload document');
        }
    }
);

export const deleteCandidateDocument = createAsyncThunk(
    'candidates/deleteDocument',
    async ({ candidateId, documentId }: { candidateId: string; documentId: string }, { rejectWithValue }) => {
        try {
            await candidateService.deleteDocument(candidateId, documentId);
            return { candidateId, documentId };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete document');
        }
    }
);

// Activities
export const fetchCandidateActivities = createAsyncThunk(
    'candidates/fetchActivities',
    async (candidateId: string, { rejectWithValue }) => {
        try {
            const activities = await candidateService.getCandidateActivities(candidateId);
            return { candidateId, activities };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch activities');
        }
    }
);

// Notes management
export const fetchCandidateNotes = createAsyncThunk(
    'candidates/fetchNotes',
    async (candidateId: string, { rejectWithValue }) => {
        try {
            const notes = await candidateService.getNotes(candidateId);
            return { candidateId, notes };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch notes');
        }
    }
);

export const addCandidateNote = createAsyncThunk(
    'candidates/addNote',
    async ({ candidateId, text }: { candidateId: string; text: string }, { rejectWithValue }) => {
        try {
            const note = await candidateService.addNote(candidateId, text);
            return { candidateId, note };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to add note');
        }
    }
);

export const updateCandidateNote = createAsyncThunk(
    'candidates/updateNote',
    async ({ noteId, text }: { noteId: string; text: string }, { rejectWithValue }) => {
        try {
            await candidateService.updateNote(noteId, text);
            return { noteId, text };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update note');
        }
    }
);

export const deleteCandidateNote = createAsyncThunk(
    'candidates/deleteNote',
    async ({ candidateId, noteId }: { candidateId: string; noteId: string }, { rejectWithValue }) => {
        try {
            await candidateService.deleteNote(noteId);
            return { candidateId, noteId };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete note');
        }
    }
);

const candidatesSlice = createSlice({
    name: 'candidates',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.candidateError = null;
        },
        clearCurrentCandidate: (state) => {
            state.currentCandidate = null;
        },
        clearCandidatesByStage: (state, action: PayloadAction<string>) => {
            delete state.candidatesByStage[action.payload];
        },
        clearCandidatesByJob: (state, action: PayloadAction<string>) => {
            delete state.candidatesByJob[action.payload];
        },
        updateCandidateInLists: (state, action: PayloadAction<Candidate>) => {
            const updatedCandidate = action.payload;

            // Update in main candidates list
            const mainIndex = state.candidates.findIndex(c => c.id === updatedCandidate.id);
            if (mainIndex !== -1) {
                state.candidates[mainIndex] = updatedCandidate;
            } else {
                state.candidates.push(updatedCandidate);
            }

            // Update in stage-specific lists
            Object.keys(state.candidatesByStage).forEach(stageId => {
                const stageIndex = state.candidatesByStage[stageId].findIndex(c => c.id === updatedCandidate.id);
                if (stageIndex !== -1) {
                    if (updatedCandidate.stageId === stageId) {
                        state.candidatesByStage[stageId][stageIndex] = updatedCandidate;
                    } else {
                        // Remove from old stage list
                        state.candidatesByStage[stageId].splice(stageIndex, 1);
                    }
                }
            });

            // Add to new stage list only if it has already been fetched - creating it here would
            // make the stage page think it's loaded and show just this one candidate
            if (updatedCandidate.stageId &&
                state.candidatesByStage[updatedCandidate.stageId] &&
                !state.candidatesByStage[updatedCandidate.stageId].find(c => c.id === updatedCandidate.id)) {
                state.candidatesByStage[updatedCandidate.stageId].push(updatedCandidate);
            }

            // Update in job-specific lists
            Object.keys(state.candidatesByJob).forEach(jobId => {
                const jobIndex = state.candidatesByJob[jobId].findIndex(c => c.id === updatedCandidate.id);
                if (jobIndex !== -1) {
                    if (updatedCandidate.jobId === jobId) {
                        state.candidatesByJob[jobId][jobIndex] = updatedCandidate;
                    } else {
                        state.candidatesByJob[jobId].splice(jobIndex, 1);
                    }
                }
            });

            // Update current candidate if it's the same
            if (state.currentCandidate?.id === updatedCandidate.id) {
                state.currentCandidate = updatedCandidate;
            }
        },
        removeCandidateFromLists: (state, action: PayloadAction<string>) => {
            const candidateId = action.payload;

            // Remove from main list
            state.candidates = state.candidates.filter(c => c.id !== candidateId);

            // Remove from stage lists
            Object.keys(state.candidatesByStage).forEach(stageId => {
                state.candidatesByStage[stageId] = state.candidatesByStage[stageId].filter(c => c.id !== candidateId);
            });

            // Remove from job lists
            Object.keys(state.candidatesByJob).forEach(jobId => {
                state.candidatesByJob[jobId] = state.candidatesByJob[jobId].filter(c => c.id !== candidateId);
            });

            // Clear current candidate if it's the deleted one
            if (state.currentCandidate?.id === candidateId) {
                state.currentCandidate = null;
            }

            // Clean up related data
            delete state.documents[candidateId];
            delete state.activities[candidateId];
            delete state.notes[candidateId];
        },
        setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
            state.pagination = { ...state.pagination, ...action.payload };
        },
        clearSearchCache: (state) => {
            state.searchCache = {};
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch all candidates
            .addCase(fetchCandidates.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCandidates.fulfilled, (state, action) => {
                state.loading = false;
                state.candidates = action.payload.items;
                state.pagination = {
                    currentPage: action.payload.pageNumber || 1,
                    totalPages: action.payload.totalPages || 1,
                    totalItems: action.payload.totalCount || action.payload.items.length,
                    pageSize: action.payload.pageSize || 20
                };
                state.lastFetched['all'] = Date.now();
            })
            .addCase(fetchCandidates.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Fetch recent candidates
            .addCase(fetchRecentCandidates.fulfilled, (state, action) => {
                state.recentCandidates = action.payload;
                state.lastFetched['recent'] = Date.now();
            })

            // Fetch candidates by stage
            .addCase(fetchCandidatesByStage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCandidatesByStage.fulfilled, (state, action) => {
                state.loading = false;
                state.candidatesByStage[action.payload.stageId] = action.payload.candidates;
                state.lastFetched[`stage-${action.payload.stageId}`] = Date.now();
            })
            .addCase(fetchCandidatesByStage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Fetch candidates by job
            .addCase(fetchCandidatesByJob.fulfilled, (state, action) => {
                state.candidatesByJob[action.payload.jobId] = action.payload.candidates;
                state.lastFetched[`job-${action.payload.jobId}`] = Date.now();
            })

            // Fetch candidate by ID
            .addCase(fetchCandidateById.pending, (state) => {
                state.candidateLoading = true;
                state.candidateError = null;
            })
            .addCase(fetchCandidateById.fulfilled, (state, action) => {
                state.candidateLoading = false;
                state.currentCandidate = action.payload;
                candidatesSlice.caseReducers.updateCandidateInLists(state, { payload: action.payload, type: '' });
            })
            .addCase(fetchCandidateById.rejected, (state, action) => {
                state.candidateLoading = false;
                state.candidateError = action.payload as string;
            })

            // Create candidate
            .addCase(createCandidate.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createCandidate.fulfilled, (state, action) => {
                state.loading = false;
                state.candidates.unshift(action.payload);

                // Add to appropriate stage list, if it has already been fetched
                if (action.payload.stageId && state.candidatesByStage[action.payload.stageId]) {
                    state.candidatesByStage[action.payload.stageId].push(action.payload);
                }

                // Add to appropriate job list
                if (action.payload.jobId) {
                    if (!state.candidatesByJob[action.payload.jobId]) {
                        state.candidatesByJob[action.payload.jobId] = [];
                    }
                    state.candidatesByJob[action.payload.jobId].push(action.payload);
                }
            })
            .addCase(createCandidate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Update candidate
            .addCase(updateCandidate.fulfilled, (state, action) => {
                candidatesSlice.caseReducers.updateCandidateInLists(state, { payload: action.payload, type: '' });
            })

            // Delete candidate
            .addCase(deleteCandidate.fulfilled, (state, action) => {
                candidatesSlice.caseReducers.removeCandidateFromLists(state, { payload: action.payload, type: '' });
            })

            // Change candidate stage
            .addCase(changeCandidateStage.fulfilled, (state, action) => {
                candidatesSlice.caseReducers.updateCandidateInLists(state, { payload: action.payload.candidate, type: '' });
            })

            // Document management
            .addCase(fetchCandidateDocuments.pending, (state, action) => {
                state.documentsLoading[action.meta.arg] = true;
            })
            .addCase(fetchCandidateDocuments.fulfilled, (state, action) => {
                state.documents[action.payload.candidateId] = action.payload.documents;
                state.documentsLoading[action.payload.candidateId] = false;
            })
            .addCase(fetchCandidateDocuments.rejected, (state, action) => {
                state.documentsLoading[action.meta.arg] = false;
            })

            .addCase(uploadCandidateDocument.fulfilled, (state, action) => {
                const { candidateId, document } = action.payload;
                if (!state.documents[candidateId]) {
                    state.documents[candidateId] = [];
                }
                state.documents[candidateId].push(document);
            })

            .addCase(deleteCandidateDocument.fulfilled, (state, action) => {
                const { candidateId, documentId } = action.payload;
                if (state.documents[candidateId]) {
                    state.documents[candidateId] = state.documents[candidateId].filter(d => d.id !== documentId);
                }
            })

            // Activities
            .addCase(fetchCandidateActivities.pending, (state, action) => {
                state.activitiesLoading[action.meta.arg] = true;
            })
            .addCase(fetchCandidateActivities.fulfilled, (state, action) => {
                state.activities[action.payload.candidateId] = action.payload.activities;
                state.activitiesLoading[action.payload.candidateId] = false;
            })
            .addCase(fetchCandidateActivities.rejected, (state, action) => {
                state.activitiesLoading[action.meta.arg] = false;
            })

            // Notes management
            .addCase(fetchCandidateNotes.pending, (state, action) => {
                state.notesLoading[action.meta.arg] = true;
            })
            .addCase(fetchCandidateNotes.fulfilled, (state, action) => {
                state.notes[action.payload.candidateId] = action.payload.notes;
                state.notesLoading[action.payload.candidateId] = false;
            })
            .addCase(fetchCandidateNotes.rejected, (state, action) => {
                state.notesLoading[action.meta.arg] = false;
            })

            .addCase(addCandidateNote.fulfilled, (state, action) => {
                const { candidateId, note } = action.payload;
                if (!state.notes[candidateId]) {
                    state.notes[candidateId] = [];
                }
                state.notes[candidateId].unshift(note);
            })

            .addCase(updateCandidateNote.fulfilled, (state, action) => {
                const { noteId, text } = action.payload;
                Object.keys(state.notes).forEach(candidateId => {
                    const noteIndex = state.notes[candidateId].findIndex(n => n.id === noteId);
                    if (noteIndex !== -1) {
                        state.notes[candidateId][noteIndex] = {
                            ...state.notes[candidateId][noteIndex],
                            text,
                            isEdited: true,
                            editedAt: new Date()
                        };
                    }
                });
            })

            .addCase(deleteCandidateNote.fulfilled, (state, action) => {
                const { candidateId, noteId } = action.payload;
                if (state.notes[candidateId]) {
                    state.notes[candidateId] = state.notes[candidateId].filter(n => n.id !== noteId);
                }
            });
    }
});

export const {
    clearError,
    clearCurrentCandidate,
    clearCandidatesByStage,
    clearCandidatesByJob,
    updateCandidateInLists,
    removeCandidateFromLists,
    setPagination,
    clearSearchCache
} = candidatesSlice.actions;

export default candidatesSlice.reducer;